// The GI.* namespace — Giraffe project data reachable from any cell.
//
// `fast-formula-parser`'s Function token is /[A-Za-z_]+[A-Za-z_0-9.]*\(/, so a
// dotted name parses as an ordinary function; no grammar fork is needed.
//
// None of these spill. A function that returns a table would need spill ranges,
// #SPILL! collision handling and its own invalidation path — and live blocks
// (giraffe/liveBlocks.ts) already deliver a whole bound table, better. GI.LIST
// and GI.FIELDS therefore take a 1-based index and are meant to be filled down.

import { FormulaError } from 'fast-formula-parser';
import type { SourceKind } from '@/model/types';
import type { AggWarning } from '@/data/aggregate';
import { aggregate, distinct, FOOTPRINT_FIELD } from '@/data/aggregate';
import { applyFilter } from '@/data/filter';
import { compileExpression } from '@/data/expression';
import type { DataRow, GiraffeData } from '@/data/types';
import { unitsOf } from '@/data/units';
import { compareValues } from '@/model/structure';

/** Every arg arrives wrapped by the parser, whatever its source. */
interface WrappedArg {
  value: unknown;
  isArray: boolean;
  isRangeRef: boolean;
  isCellRef: boolean;
}

export interface GiCallSite {
  sheet: string;
  row: number;
  col: number;
}

export interface GiDiagnostic extends AggWarning {
  at: GiCallSite;
  fn: string;
}

/** Collects warnings for one recalculation pass. Reset by the engine. */
export interface GiSink {
  push(d: GiDiagnostic): void;
}

const SOURCES: readonly SourceKind[] = ['areas', 'bakedSections', 'rawSections', 'usages'];

function argValue(a: WrappedArg | undefined): unknown {
  return a === undefined ? undefined : a.value;
}

/** `#VALUE!`, `#REF!` — what a failed sub-expression leaves in an argument. */
const ERROR_TEXT = /^#[A-Z0-9/]+[!?]$/;

/**
 * An argument as text.
 *
 * An ERROR arriving here is not a field name. `GI.TOTAL("dwelling-"+A3+"-Count")`
 * uses `+` where a spreadsheet wants `&`, so the argument is already `#VALUE!`
 * before GI.TOTAL is called — and stringifying it asked the project for a field
 * called "#VALUE!", which nothing carries, which is 0. A broken formula
 * answered with a plausible number. It rethrows now, so the cell says what
 * happened.
 */
function asText(a: WrappedArg | undefined, fallback = ''): string {
  const v = argValue(a);
  if (v === undefined || v === null) return fallback;
  const text = String(v);
  if (ERROR_TEXT.test(text)) throw new FormulaError(text, `An argument came in as ${text}. In a spreadsheet, text is joined with & — "a"&B1 — not with +.`);
  return text;
}

/** A property as a number, or null when it is not one. */
/**
 * Quote a filter value, but only when leaving it bare would change the meaning.
 *
 * A comma would otherwise split the clause in two — "Retail, ground" read as a
 * usage and a second clause that is not one. Everything else stays bare, so a
 * filter built by GI.WHERE reads exactly like one typed by hand.
 */
function quoteFilterValue(value: string): string {
  if (!/[,'"]/.test(value)) return value;
  if (!value.includes('"')) return `"${value}"`;
  return `'${value.replace(/'/g, '')}'`;
}

function numberOf(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function asIndex(a: WrappedArg | undefined): number {
  const v = argValue(a);
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) throw FormulaError.VALUE;
  return Math.trunc(n);
}

/**
 * `[source]` is no longer a documented part of any `GI.*` signature — every
 * formula reads the Areas sheet's own rows by default now (one per baked
 * section, split further by level; see `data/areasTable.ts`), not Giraffe's
 * raw `bakedSections`. The explicit override still works, UNDOCUMENTED, for
 * backward compatibility: nothing already written with a literal
 * `"rawSections"`/`"usages"`/`"bakedSections"` third argument stops working.
 */
function asSource(a: WrappedArg | undefined, fallback: SourceKind): SourceKind {
  const v = asText(a, '').trim();
  if (v === '') return fallback;
  const found = SOURCES.find((s) => s.toLowerCase() === v.toLowerCase());
  if (!found) throw new FormulaError('#NAME?', `unknown source "${v}" — expected one of ${SOURCES.join(', ')}`);
  return found;
}

/** `"desc"`/`"descending"` (any case) for reversed order; blank or anything else is ascending. */
function asDescending(a: WrappedArg | undefined): boolean {
  return /^desc/i.test(asText(a, '').trim());
}

/**
 * Resolve rows for a call: source, then filter, then (when the workbook asked
 * for it) the map selection. Filter syntax errors surface as #VALUE! rather
 * than quietly matching everything — a filter that silently does nothing is
 * the failure mode that produces a confidently wrong total.
 */
function resolveRows(data: GiraffeData, source: SourceKind, filter: string): readonly DataRow[] {
  const all = data.rows(source);
  let byFilter = filtered.get(all);
  if (byFilter === undefined) {
    byFilter = new Map();
    filtered.set(all, byFilter);
  }
  let hit = byFilter.get(filter);
  if (hit === undefined) {
    hit = applyFilter(all, filter);
    byFilter.set(filter, hit);
  }
  if (hit.errors.length > 0) throw new FormulaError('#VALUE!', hit.errors[0]);
  return hit.rows;
}

/**
 * Filtered rows per source array, per filter text. A schedule asks the same
 * question in every column (`GI.WHERE("usageName", D34)` three times in one
 * cell, across a dozen cells per row), and each ask used to re-filter every
 * Areas row. Keyed by the row array's identity, which the provider replaces
 * whenever the data changes, so a stale answer cannot outlive its data.
 */
const filtered = new WeakMap<readonly DataRow[], Map<string, { rows: DataRow[]; errors: string[] }>>();

export interface GiFunctionOptions {
  data: () => GiraffeData;
  sink: GiSink;
  /**
   * The filter of the sheet a formula sits on, for one source — "" for none.
   * ANDed into every call, so a sheet filtered to layer 2 is about layer 2
   * from its first GI.TOTAL to its last. See model/sheetFilter.ts.
   */
  sheetFilter?: (sheetName: string, source: SourceKind, data: GiraffeData) => string;
  /** The source a call reads when it names none — the workbook's `giSource`. Defaults to `'areas'`. */
  defaultSource?: () => SourceKind;
}

/**
 * Build the `functionsNeedContext` bag. Context is used only to attribute a
 * warning to the cell that caused it — the values themselves never depend on
 * position, so these stay ordinary pure functions of (data, args).
 */
export function makeGiFunctions(opts: GiFunctionOptions): Record<string, (ctx: unknown, ...args: never[]) => unknown> {
  const { data, sink } = opts;
  const sourceOf = (a: WrappedArg | undefined): SourceKind => asSource(a, opts.defaultSource?.() ?? 'areas');

  const at = (ctx: unknown): GiCallSite => {
    const pos = (ctx as { position?: GiCallSite } | undefined)?.position;
    return pos ?? { sheet: '', row: 0, col: 0 };
  };

  /** The rows a call sees: its own filter AND the filter of the sheet it is on. */
  const rowsFor = (ctx: unknown, d: GiraffeData, source: SourceKind, filter: string): readonly DataRow[] => {
    const scope = opts.sheetFilter?.(at(ctx).sheet, source, d) ?? '';
    return resolveRows(d, source, scope === '' ? filter : filter.trim() === '' ? scope : `${filter}, ${scope}`);
  };

  const report = (ctx: unknown, fn: string, warnings: readonly AggWarning[]): void => {
    if (warnings.length === 0) return;
    const site = at(ctx);
    for (const w of warnings) sink.push({ ...w, at: site, fn });
  };

  /**
   * A field NOBODY carries is a typo, and a typo here is silent: the aggregate
   * has nothing to add up, so it answers 0 — a real-looking number in a real
   * proforma.
   *
   * Worth naming precisely because the field names are not guessable. Giraffe
   * flattens a usage's dwelling mix into one property per key, and the key is
   * whatever the user typed into the mix: `dwelling-1 bed-Count` in one project,
   * `dwelling-1BED-Count` in another. Which is also why the near-miss check
   * ignores case and punctuation — those two ARE the same field, spelled by two
   * different hands, and saying so is more use than saying "unknown".
   */
  const resolveField = (ctx: unknown, fn: string, d: GiraffeData, source: SourceKind, field: string): string => {
    const known = d.fields(source);
    if (known.includes(field)) return field;
    const loose = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = loose(field);
    const near = known.filter((k) => loose(k) === target);
    /*
     * Warn but ALLOW, as everywhere else here: one field differing only in case
     * or punctuation is the field that was meant — `dwelling-1 bed-count` for
     * `dwelling-1 bed-Count` — and answering 0 to punish the capital letter
     * helps nobody. Two matches is genuinely ambiguous, so that stays a miss.
     */
    if (near.length === 1) {
      sink.push({
        kind: 'unknown-field',
        field,
        message: `Read "${field}" as "${near[0]}" — the project spells it that way.`,
        at: at(ctx),
        fn,
      });
      return near[0]!;
    }
    sink.push({
      kind: 'unknown-field',
      field,
      message: `Nothing in ${source} carries "${field}". The field names this project has are the columns of the Features sheet.`,
      at: at(ctx),
      fn,
    });
    return field;
  };

  /** One compile per formula, then a closure called once per feature. */
  const compile = (src: string) => {
    if (!src.trim()) throw new FormulaError('#VALUE!', 'The expression is empty.');
    try {
      return compileExpression(src);
    } catch (err) {
      throw new FormulaError('#VALUE!', err instanceof Error ? err.message : 'Cannot read the expression.');
    }
  };

  /**
   * The loop.
   *
   * A feature that is missing ANY field the expression names is SKIPPED — it
   * contributes nothing at all, not a zero. A zero would be a claim: it says
   * this section costs nothing, which drags an average down and makes a MINEACH
   * answer zero. Absent is not zero, and the two must not be confused.
   *
   * Skipping silently is its own trap, though: a sum over forty sections where
   * eleven had no `hardCost` is a smaller number that looks exactly like a
   * correct one. So every skip is counted and reported, and the cell carries a
   * warning saying how many went and which field they were missing.
   */
  const each =
    (fn: string, reduce: (values: number[]) => number) =>
    (ctx: unknown, ...args: WrappedArg[]): number => {
      const d = data();
      const compiled = compile(asText(args[0]));
      const source = sourceOf(args[2]);
      const rows = rowsFor(ctx, d, source, asText(args[1]));
      const values: number[] = [];
      /** How many features each missing field cost us. */
      const missing = new Map<string, number>();
      let noAnswer = 0;

      for (const row of rows) {
        const value = compiled((name) => numberOf(row.properties[name]));
        if (value !== null) {
          values.push(value);
          continue;
        }
        // Null means either a field this feature does not have, or arithmetic
        // with no answer — a division by zero. Worth telling apart: one is
        // missing data, the other is a formula that cannot apply here.
        const absent = compiled.fields.filter((f) => numberOf(row.properties[f]) === null);
        if (absent.length === 0) noAnswer += 1;
        else for (const f of absent) missing.set(f, (missing.get(f) ?? 0) + 1);
      }

      const warnings: AggWarning[] = [];
      for (const [field, n] of missing) {
        warnings.push({
          kind: 'skipped',
          field,
          message: `${fn}: ${n} of ${rows.length} skipped — no ${field}. They add nothing rather than zero.`,
        });
      }
      if (noAnswer > 0) {
        warnings.push({
          kind: 'skipped',
          field: '',
          message: `${fn}: ${noAnswer} of ${rows.length} skipped — the expression had no answer there (a division by zero).`,
        });
      }
      report(ctx, fn, warnings);

      if (values.length === 0) {
        if (fn === 'GI.SUMEACH') return 0;
        throw new FormulaError('#DIV/0!', `No feature had every field ${fn} needs.`);
      }
      return reduce(values);
    };

  const agg =
    (fn: string, kind: 'sum' | 'avg' | 'min' | 'max' | 'weighted') =>
    (ctx: unknown, ...args: WrappedArg[]): number | string => {
      const d = data();
      const field = asText(args[0]).trim();
      if (!field) throw new FormulaError('#VALUE!', `${fn} needs a field name, e.g. ${fn}("grossArea")`);
      const source = sourceOf(args[2]);
      const rows = rowsFor(ctx, d, source, asText(args[1]));
      const name = resolveField(ctx, fn, d, source, field);
      const res = aggregate(rows, name, kind, d.classOf(source, name));
      report(ctx, fn, res.warnings);
      // The GI aggregates are numeric by contract; a text or nested field
      // reaching one is a mistake worth naming rather than a silent 0.
      if (res.value === null) return 0;
      if (typeof res.value !== 'number') {
        throw new FormulaError('#VALUE!', `${name} is not a number — use GI.VALUE("${name}", …) to read it`);
      }
      return res.value;
    };

  const fns: Record<string, (ctx: unknown, ...args: WrappedArg[]) => unknown> = {
    // -- aggregates ---------------------------------------------------------
    /** GI.TOTAL(field, [filter]) — sum. */
    'GI.TOTAL': agg('GI.TOTAL', 'sum'),
    /** Alias; people reach for SUM before TOTAL. */
    'GI.SUM': agg('GI.SUM', 'sum'),
    /**
     * GI.WEIGHTEDAVG(field, [filter]) — the mean weighted by floor
     * area.
     *
     * The reduction a per-unit number needs. Adding $8,700/m² to $6,000/m²
     * gives $14,700/m², which is not a price; the answer is the average, and it
     * has to be weighted by the floor area each rate applies to or a tower
     * counts the same as a kiosk.
     *
     * This was called GI.RATE, which named the KIND of field it suited rather
     * than what it did, and nobody could tell from the name what they would
     * get.
     */
    'GI.WEIGHTEDAVG': agg('GI.WEIGHTEDAVG', 'weighted'),
    'GI.AVG': agg('GI.AVG', 'avg'),
    'GI.MIN': agg('GI.MIN', 'min'),
    'GI.MAX': agg('GI.MAX', 'max'),

    /**
     * GI.FOOTPRINT([filter]) — the ground each section covers, one
     * level per section. NOT the site: see GI.SITEAREA.
     */
    'GI.FOOTPRINT': (_ctx: unknown, ...args: WrappedArg[]): number => {
      const d = data();
      const source = sourceOf(args[1]);
      const rows = rowsFor(_ctx, d, source, asText(args[0]));
      const total = aggregate(rows, FOOTPRINT_FIELD, 'sum', 'plan').value;
      return typeof total === 'number' ? total : 0;
    },

    /**
     * GI.SITEAREA() — the area of the PROJECT BOUNDARY.
     *
     * A different number from the footprint, and the one a plot ratio divides
     * by. Dividing floor area by footprint instead gives floor area per m² of
     * building, which overstates the ratio by however much of the site is not
     * built on.
     *
     * In the sheet's units, like every other area here — so a plot ratio comes
     * out the same in either system, both halves of it having moved together.
     */
    'GI.SITEAREA': (): number => {
      const site = data().siteArea();
      if (site === null) {
        throw new FormulaError(
          '#N/A',
          'No project boundary to measure. GI.SITEAREA reads the project polygon; if this project has none, use GI.FOOTPRINT() for the ground the buildings cover.'
        );
      }
      return site;
    },

    /** GI.COUNTUNIQUE(field, [filter]) — how many distinct values. */
    'GI.COUNTUNIQUE': (_ctx: unknown, ...args: WrappedArg[]): number => {
      const d = data();
      let field = asText(args[0]).trim();
      if (!field) throw new FormulaError('#VALUE!', 'GI.COUNTUNIQUE needs a field name');
      const source = sourceOf(args[2]);
      field = resolveField(_ctx, 'GI.COUNTUNIQUE', d, source, field);
      const rows = rowsFor(_ctx, d, source, asText(args[1]));
      return distinct(rows, field).length;
    },

    /** GI.COUNT([filter]) — how many features match. */
    'GI.COUNT': (_ctx: unknown, ...args: WrappedArg[]): number => {
      const d = data();
      const source = sourceOf(args[1]);
      return rowsFor(_ctx, d, source, asText(args[0])).length;
    },

    /**
     * GI.SUMPRODUCT(fieldA, fieldB, [filter]) — the sum of the
     * PRODUCTS, feature by feature.
     *
     * This is not the same as multiplying two totals, and the difference is the
     * whole point. `GI.TOTAL("area") * GI.WEIGHTEDAVG("hardCost")` multiplies an area
     * by an average rate; it is only right when every section shares that rate.
     * Where the rate varies — which is the normal case — the answer is
     * Σ(areaᵢ × costᵢ), and nothing else gives it.
     */
    'GI.SUMPRODUCT': (_ctx: unknown, ...args: WrappedArg[]): number => {
      const d = data();
      const a = asText(args[0]).trim();
      const b = asText(args[1]).trim();
      if (!a || !b) throw new FormulaError('#VALUE!', 'GI.SUMPRODUCT needs two field names');
      const source = sourceOf(args[3]);
      const rows = rowsFor(_ctx, d, source, asText(args[2]));
      let total = 0;
      for (const row of rows) {
        const x = numberOf(row.properties[a]);
        const y = numberOf(row.properties[b]);
        if (x === null || y === null) continue;
        total += x * y;
      }
      return total;
    },

    /**
     * GI.NTH(field, index, [filter]) — the field off the nth matching
     * feature, 1-based.
     *
     * The loop, one row at a time: stamp `GI.NTH("name",ROW()-1)` down a column
     * beside `GI.NTH("area",ROW()-1)` and `GI.NTH("hardCost",ROW()-1)`, and the
     * arithmetic between them is ordinary spreadsheet arithmetic. Past the last
     * match it returns "", so the tail of a stamped range stays blank.
     */
    'GI.NTH': (_ctx: unknown, ...args: WrappedArg[]): string | number | boolean => {
      const d = data();
      let field = asText(args[0]).trim();
      if (!field) throw new FormulaError('#VALUE!', 'GI.NTH needs a field name');
      const index = asIndex(args[1]);
      const source = sourceOf(args[3]);
      field = resolveField(_ctx, 'GI.NTH', d, source, field);
      const rows = rowsFor(_ctx, d, source, asText(args[2]));
      if (index < 1 || index > rows.length) return '';
      const v = rows[index - 1]!.properties[field];
      if (v == null) return '';
      if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') return v;
      return String(v);
    },

    /**
     * GI.SUMEACH(expression, [filter]) — evaluate the expression
     * against EVERY feature, then add the results up.
     *
     *   =GI.SUMEACH("area * hardCost")
     *   =GI.SUMEACH("area * salePrice / levels", "usage=Residential")
     *
     * This is the loop. Aggregating first and then doing the arithmetic —
     * `GI.TOTAL("area") * GI.WEIGHTEDAVG("hardCost")` — is a different and
     * usually wrong number, because it multiplies a total by an average rather
     * than adding up what each section actually costs.
     *
     * Field names are bare (`area`) or bracketed when they contain anything
     * awkward: `[dwelling-1BED-Count]`, which read bare would be three
     * subtractions.
     *
     * A feature MISSING one of the fields is skipped, not counted as zero, and
     * the cell says how many went and which field they lacked. See `each`.
     */
    'GI.SUMEACH': each('GI.SUMEACH', (values) => values.reduce((a, b) => a + b, 0)),
    'GI.AVGEACH': each('GI.AVGEACH', (values) => values.reduce((a, b) => a + b, 0) / values.length),
    'GI.MINEACH': each('GI.MINEACH', (values) => Math.min(...values)),
    'GI.MAXEACH': each('GI.MAXEACH', (values) => Math.max(...values)),

    /**
     * GI.EACH(expression, index, [filter]) — the expression evaluated
     * against the nth feature alone.
     *
     * The loop written out down a column, when the total is not enough and the
     * working has to be visible.
     */
    'GI.EACH': (_ctx: unknown, ...args: WrappedArg[]): number | string => {
      const d = data();
      const compiled = compile(asText(args[0]));
      const index = asIndex(args[1]);
      const source = sourceOf(args[3]);
      const rows = rowsFor(_ctx, d, source, asText(args[2]));
      if (index < 1 || index > rows.length) return '';
      const value = compiled((name) => numberOf(rows[index - 1]!.properties[name]));
      return value ?? '';
    },

    /**
     * GI.WHERE(field, value, …) — build a filter without the quote arithmetic.
     *
     *   GI.SUMEACH("grossArea * hardCost", GI.WHERE("usage", $A20))
     *
     * The same thing as writing `"usage="&$A20`, and the reason it exists is
     * that nobody should have to work that out. A filter is TEXT, so a value
     * coming from a cell has to be joined onto it with `&`, and the quotes
     * around `usage=` are just the quotes any text argument needs — obvious
     * once you know, and nothing on screen tells you.
     *
     * Pairs, so several clauses read in a row:
     *
     *   GI.WHERE("usage", $A20, "levels>", 3)
     *
     * An operator glued to the field is kept (`"levels>"`); without one, `=`.
     * Values are quoted only when they need it, so what you read back is the
     * filter you would have written by hand.
     */
    'GI.WHERE': (_ctx: unknown, ...args: WrappedArg[]): string => {
      const parts: string[] = [];
      for (let i = 0; i < args.length; i += 2) {
        const field = asText(args[i]).trim();
        if (field === '') continue;
        const raw = argValue(args[i + 1]);
        const value = raw === null || raw === undefined ? '' : String(raw);
        const op = /[=!<>~]+$/.test(field) ? '' : '=';
        parts.push(`${field}${op}${quoteFilterValue(value)}`);
      }
      if (parts.length === 0) {
        throw new FormulaError('#VALUE!', 'GI.WHERE needs a field and a value, e.g. GI.WHERE("usage", A2)');
      }
      return parts.join(', ');
    },

    /**
     * GI.UNITS() — "imperial" or "metric", the units every number above is in.
     *
     * Worth having in a cell because a schedule is usually read away from the
     * app that made it: a title row saying =GI.UNITS() labels the whole sheet,
     * and it follows the project rather than being a note that goes stale. The
     * project's own setting is what decides it (giraffe/units.ts), unless the
     * workbook overrides it in the toolbar.
     */
    'GI.UNITS': (): string => unitsOf(data()),

    /**
     * GI.CRITERIA(field, value, [field2, value2, …]) — GI.WHERE for a filter
     * someone PICKS: a blank value means "any", so the pair drops out rather
     * than matching the features with no value. Nothing picked is "", which
     * every GI function reads as no filter at all — an untouched dropdown must
     * not empty everything that reads it.
     */
    'GI.CRITERIA': (_ctx: unknown, ...args: WrappedArg[]): string => {
      const parts: string[] = [];
      for (let i = 0; i < args.length; i += 2) {
        const field = asText(args[i]).trim();
        const raw = argValue(args[i + 1]);
        const value = raw === null || raw === undefined ? '' : String(raw).trim();
        if (field === '' || value === '') continue;
        const op = /[=!<>~]+$/.test(field) ? '' : '=';
        parts.push(`${field}${op}${quoteFilterValue(value)}`);
      }
      return parts.join(', ');
    },

    /**
     * GI.ALL(filter, [filter2, …]) — every filter at once. Blanks are skipped,
     * so two filters — a fixed one and one from a cell — combine without the
     * `&", "&` a joined string would need.
     */
    'GI.ALL': (_ctx: unknown, ...args: WrappedArg[]): string =>
      args
        .map((a) => asText(a).trim())
        .filter((f) => f !== '')
        .join(', '),

    // -- discovery ----------------------------------------------------------
    /**
     * GI.LIST(field, index, [filter], [source], [sort]) — nth distinct value,
     * sorted ascending unless [sort] is "desc"/"descending". Fill down.
     */
    'GI.LIST': (_ctx: unknown, ...args: WrappedArg[]): string | number => {
      const d = data();
      let field = asText(args[0]).trim();
      if (!field) throw new FormulaError('#VALUE!', 'GI.LIST needs a field name');
      const idx = asIndex(args[1]);
      const source = sourceOf(args[3]);
      const descending = asDescending(args[4]);
      field = resolveField(_ctx, 'GI.LIST', d, source, field);
      const rows = rowsFor(_ctx, d, source, asText(args[2]));
      const values = distinct(rows, field).sort(compareValues);
      if (descending) values.reverse();
      if (idx < 1 || idx > values.length) return '';
      return values[idx - 1]!;
    },

    /** GI.FIELDS(index) — nth property name. Fill down to explore. */
    'GI.FIELDS': (_ctx: unknown, ...args: WrappedArg[]): string => {
      const d = data();
      const idx = asIndex(args[0]);
      const source = sourceOf(args[1]);
      const fields = d.fields(source);
      if (idx < 1 || idx > fields.length) return '';
      return fields[idx - 1]!;
    },

    /** GI.CLASS(field) — plan | total | rate | text. */
    'GI.CLASS': (_ctx: unknown, ...args: WrappedArg[]): string => {
      const d = data();
      const field = asText(args[0]).trim();
      if (!field) throw new FormulaError('#VALUE!', 'GI.CLASS needs a field name');
      return d.classOf(sourceOf(args[1]), field);
    },

    /** GI.VALUE(field, filter) — the field off the first match. */
    'GI.VALUE': (_ctx: unknown, ...args: WrappedArg[]): string | number | boolean => {
      const d = data();
      let field = asText(args[0]).trim();
      if (!field) throw new FormulaError('#VALUE!', 'GI.VALUE needs a field name');
      const source = sourceOf(args[2]);
      field = resolveField(_ctx, 'GI.VALUE', d, source, field);
      const rows = rowsFor(_ctx, d, source, asText(args[1]));
      const first = rows[0];
      if (!first) return '';
      const v = first.properties[field];
      if (v == null) return '';
      if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') return v;
      return String(v);
    },
  };

  // `GI.AREA` was ambiguous — area of what? It keeps working as an alias of the
  // explicit name so no sheet already using it breaks.
  fns['GI.AREA'] = fns['GI.FOOTPRINT']!;

  // GI.RATE is REMOVED, not aliased. A sheet still using it gets a signpost
  // rather than a bare #NAME?, which says nothing about where the function
  // went.
  fns['GI.RATE'] = () => {
    throw new FormulaError('#NAME?', 'GI.RATE is now GI.WEIGHTEDAVG — the mean weighted by floor area.');
  };

  return fns as unknown as Record<string, (ctx: unknown, ...args: never[]) => unknown>;
}

/** Names registered above — the engine uses this to mark volatile formulas. */
export const GI_FUNCTION_NAMES = [
  'GI.TOTAL',
  'GI.SUM',
  'GI.AVG',
  'GI.MIN',
  'GI.MAX',
  'GI.WEIGHTEDAVG',
  'GI.SUMEACH',
  'GI.AVGEACH',
  'GI.MINEACH',
  'GI.MAXEACH',
  'GI.EACH',
  'GI.FOOTPRINT',
  'GI.AREA',
  'GI.SITEAREA',
  'GI.COUNT',
  'GI.COUNTUNIQUE',
  'GI.SUMPRODUCT',
  'GI.NTH',
  'GI.LIST',
  'GI.FIELDS',
  'GI.CLASS',
  'GI.VALUE',
  'GI.UNITS',
] as const;
